import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import validator from 'validator';
import {
  Typography,
  Button,
  Input,
  InputContainer,
  BorderLine,
  DatePicker,
  BelongsToSelects,
  getMultipleValue,
  Select,
  InputNumber,
  enqueueSnackbar,
} from '../../design-system';
import {
  DashboardHeader,
  DashboardForm,
  DashboardFormTwoColumn,
  DashboardFormCustomColumn,
} from '../dashboard';
import { useCreateGateOutsFilterMutation } from '../../hooks';
import { containerTypesLabel, containerValidator, getNormalFormProps } from '../../utils';
import {
  containerConditionsLabel,
  gateOutCreateModeOptions,
  INPUT_ERRORS,
  MESSAGES,
  REGEX,
} from '../../const';
import { i18n } from '../../i18n';

let formProps: any = {};

const getSubmitProps = () => {
  const props: any = {
    yardName: formProps.yardName,
    clientName: formProps.clientName,
    containerNumber: true,
    licensePlateNumber: !!formProps.licensePlateNumber.length,
  };

  const validate: any = {};

  ['containerNumber', 'licensePlateNumber'].forEach((key) => {
    const list: any[] = Array.isArray(formProps[key])
      ? formProps[key]
      : key === 'containerNumber' && Array.isArray(formProps.containers)
      ? formProps.containers
      : [];

    list.forEach((val: any, i: any) => {
      if (key === 'licensePlateNumber') {
        validate['licensePlateNumber-' + (i + 1)] = REGEX.LICENSE_PLATE_NUMBER.test(val) && !!val;
        validate['licensePlateNumberLength-' + (i + 1)] =
          validator.isLength(val, {
            min: 4,
            max: 10,
          }) && !!val;
        return;
      }

      props[key + '-' + (i + 1)] = containerValidator(val) ? val : '';
    });
  });
  const errors: any = {
    validFrom: moment(formProps.validFrom).set('hour', 23).isAfter(moment().set('hour', 0)),
    validTo: moment(formProps.validTo)
      .set('hour', 23)
      .isAfter(moment(formProps.validFrom).set('hour', 0)),
    hasValidFrom: !!formProps.validFrom,
    hasValidTo: !!formProps.validTo,
  };
  const { validators }: any = formProps;

  if (
    [
      ...Object.values(props),
      ...Object.values(validate),
      ...Object.values(errors),
      ...Object.values(validators).map((err) => !err),
    ].some((val) => !val)
  ) {
    return;
  }

  delete formProps.isSubmitClicked;

  return getNormalFormProps(formProps);
};

export const GateOutsAddHeader = ({ showOnlySubmit }: any) => {
  const navigate = useNavigate();
  const { mutate } = useCreateGateOutsFilterMutation();

  const handleSubmitClick = async () => {
    formProps.onSubmitClick();
    const submitProps = getSubmitProps();
    if (!submitProps) return;

    mutate(submitProps, {
      onSuccess: (res) => {
        enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.CREATED_FILTER(res.data.count), 'success');
        navigate('/dashboard/gateouts');
      },
      onError: () => {
        enqueueSnackbar(MESSAGES.GATE_OUT.ERROR.NOT_CREATED_FILTER, 'error');
      },
    });
  };

  return (
    <DashboardHeader>
      {!showOnlySubmit && (
        <>
          <Typography variant={'textXL'} weight={'bold'}>
            {i18n.t('pages.gateOutsAdd.title')}
          </Typography>
          <Button size={'medium'} data-color={'white'} to={-1}>
            {i18n.t('button.goBack')}
          </Button>
        </>
      )}
      {showOnlySubmit && <span />}
      <Button size={'medium'} onClick={handleSubmitClick}>
        {i18n.t('button.submit')}
      </Button>
    </DashboardHeader>
  );
};

export const GateOutsAddForm = ({ createMode, setCreateMode }: any) => {
  const [yardName, setYardName] = useState('');
  const yardId = useRef('');
  const [clientName, setClientName] = useState('');
  const clientId = useRef('');
  const [requestedCount, setRequestedCount] = useState('');
  const [type, setType] = useState('');
  const [condition, setCondition] = useState('');
  const [containers, setContainers] = useState<string[]>([]);
  const [containersText, setContainersText] = useState('');
  const [validFrom, setValidFrom] = useState(moment());
  const [validTo, setValidTo] = useState(moment().add(7, 'day'));
  const [transport] = useState('ground');
  const [licensePlateNumber, setLicensePlateNumber] = useState<string[]>([]);

  const [updateKey, setUpdate] = useState<any>(Math.random());

  const containerTypeOptions = Object.keys(containerTypesLabel).map((value) => ({
    label: containerTypesLabel[value as keyof typeof containerTypesLabel],
    value,
  }));

  const containerConditionOptions = Object.keys(containerConditionsLabel).map((value) => ({
    label: containerConditionsLabel[value as keyof typeof containerConditionsLabel],
    value,
  }));

  const validators: any = {
    containerNumber:
      containers.length && !containers.every((val) => containerValidator(val))
        ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER
        : '',
  };

  useEffect(() => {
    if (!formProps) return;

    formProps.isSubmitClicked = false;
    setUpdate(Math.random());
  }, []);

  useEffect(() => {
    formProps = {
      ...formProps,
      yardName,
      yardId: yardId.current,
      clientName,
      clientId: clientId.current,
      requestedCount: requestedCount ? Number(requestedCount) : undefined,
      type,
      condition,
      containers,
      validFrom,
      validTo,
      transport,
      licensePlateNumber,
      validators,
      onSubmitClick: () => {
        formProps.isSubmitClicked = true;
        setUpdate(Math.random());
      },
    };
  }, [
    yardName,
    yardId.current,
    clientName,
    clientId.current,
    requestedCount,
    type,
    condition,
    containers.join(''),
    validFrom,
    validTo,
    transport,
    licensePlateNumber.join(''),
    licensePlateNumber.length,
  ]);

  return (
    <DashboardForm paperSize={'full-size'}>
      <DashboardFormCustomColumn columns="max-content">
        <Select
          className={'select-create-mode'}
          value={createMode}
          options={gateOutCreateModeOptions}
          onChange={(e) => setCreateMode(e.target.value)}
        />
      </DashboardFormCustomColumn>
      <BelongsToSelects
        yardId={yardId.current}
        yardName={yardName}
        clientId={clientId.current}
        clientName={clientName}
        handleYardChange={(obj) => {
          yardId.current = obj.id;
          setYardName(obj.label);
        }}
        handleClientChange={(obj) => {
          clientId.current = obj.id;
          setClientName(obj.label);
        }}
        isSubmitClicked={formProps.isSubmitClicked}
      />
      <DashboardFormCustomColumn columns={'1fr 1fr 1fr'} align={'start'}>
        <InputNumber
          label={i18n.t('label.requestedCount')}
          placeholder={i18n.t('placeholder.defaultAll')}
          data-size={'medium'}
          value={requestedCount}
          onChange={(e: any) => setRequestedCount(e.target.value)}
        />
        <Select
          label={i18n.t('label.type')}
          options={containerTypeOptions}
          data-size={'medium'}
          value={type}
          onChange={(e: any) => setType(e.target.value)}
          withClearButton
        />
        <Select
          label={i18n.t('label.condition')}
          options={containerConditionOptions}
          data-size={'medium'}
          value={condition}
          onChange={(e: any) => setCondition(e.target.value)}
          withClearButton
        />
      </DashboardFormCustomColumn>
      <DashboardFormCustomColumn columns={'1fr 1fr'} align={'start'}>
        <InputContainer
          label={i18n.t('label.containerNumbers')}
          placeholder={'AAAZ1234567\nAAAZ1234567\nAAAZ1234567\nAAAZ1234567'}
          data-size={'medium'}
          rows={4}
          multiline
          value={containersText}
          onChange={(e: any) => {
            const nextText = String(e?.target?.value ?? '');
            setContainersText(nextText);

            const next = nextText
              .split(/[\n,]+/g)
              .map((v) => v.trim())
              .filter(Boolean);
            setContainers(next);
          }}
          error={(!!containers.length || formProps.isSubmitClicked) && !!validators.containerNumber}
          helperText={
            (!!containers.length || formProps.isSubmitClicked) && validators.containerNumber
          }
        />
        <Input
          key={updateKey}
          label={i18n.t('label.licensePlateNumber') + '*'}
          placeholder={'10Z234EB,20Z234EC,30Z334EC'}
          data-size={'medium'}
          defaultValue={licensePlateNumber.join(',')}
          onChange={(e) => {
            const value = getMultipleValue(e);
            setLicensePlateNumber(value);
          }}
          error={
            (formProps.isSubmitClicked && !licensePlateNumber.length) ||
            (licensePlateNumber.length &&
              (licensePlateNumber.some(
                (val: any) => !validator.isLength(val, { min: 4, max: 10 }),
              ) ||
                licensePlateNumber.some((val: any) => !REGEX.LICENSE_PLATE_NUMBER.test(val))))
          }
          helperText={
            formProps.isSubmitClicked && !licensePlateNumber.length
              ? INPUT_ERRORS.REQUIRED.LICENSE_PLATE_NUMBER
              : licensePlateNumber.length &&
                licensePlateNumber.some((val: any) => !validator.isLength(val, { min: 4, max: 10 }))
              ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER_LENGTH
              : licensePlateNumber.some((val: any) => !REGEX.LICENSE_PLATE_NUMBER.test(val))
              ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER
              : ''
          }
        />
      </DashboardFormCustomColumn>
      <BorderLine space={'dashboard-form-dates'} />
      <Typography variant={'textS'} color={'--gray-80'}>
        {i18n.t('label.gateOutDate')}
      </Typography>
      <DashboardFormTwoColumn>
        <DatePicker
          label={i18n.t('label.from') + '*'}
          data-size={'medium'}
          defaultValue={validFrom}
          onChange={setValidFrom}
          minDate={moment()}
          maxDate={validTo ? moment(validTo) : null}
          withoutClearIcon
          error={formProps.isSubmitClicked && !validFrom}
          helperText={formProps.isSubmitClicked && !validFrom && INPUT_ERRORS.REQUIRED.DATE}
        />
        <DatePicker
          label={i18n.t('label.to') + '*'}
          data-size={'medium'}
          defaultValue={validTo}
          onChange={setValidTo}
          minDate={validFrom ? moment(validFrom) : moment()}
          disabled={!validFrom}
          withoutClearIcon
          error={formProps.isSubmitClicked && !validTo}
          helperText={formProps.isSubmitClicked && !validTo && INPUT_ERRORS.REQUIRED.DATE}
        />
      </DashboardFormTwoColumn>
    </DashboardForm>
  );
};
